import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CollateralBasketType,
  EntityState,
  EntityTypeEnum,
  MarketStatus,
  ProductSector,
  ReferenceDataManagementEvent,
  WorkflowActions
} from 'api'
import { Locale, Translation } from 'locale'
import { SectorDialogComponent } from './SectorDialogComponent'
import { ClonedComponentByShowModal, NotificationType, useModal } from 'view/shared/modal'
import { useProductGetter, useProductSectorGetter, useProductSectorReferenceData, useReferenceDataAvailableEvents } from 'view/hooks'
import { marketDetailGetterSelector } from 'selectors/platformSelectors'
import { UnhappyError, withUnhappyModalBoundary } from 'view/shared/error'

interface SectorDialogProps extends ClonedComponentByShowModal {
  workflowId: string
  entityId: string
}

export const SectorEditDialog: React.FC<SectorDialogProps> = withUnhappyModalBoundary(({ workflowId, onClose, entityId }) => {
  const dispatch = useDispatch()
  const { showDialog } = useModal()
  const productSectorEntity = useProductSectorReferenceData({ workflowId, entityId })
  const availableEvents = useReferenceDataAvailableEvents(workflowId)
  const getProductSector = useProductSectorGetter()
  const getProduct = useProductGetter()
  const getMarket = useSelector(marketDetailGetterSelector)

  if (!productSectorEntity) {
    throw new UnhappyError('Product entity sector not found.')
  }

  const productSector = productSectorEntity?.data
  const product = getProduct(productSector?.productId)
  const [initialMarket] = useState(() => getMarket(product?.market))

  const title = Locale.translate(
    productSectorEntity?.entityState === EntityState.NEW ? Translation.TC_NEW_SECTOR : Translation.TC_EDIT_SECTOR
  )

  const cannotUpdate = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)

  const handleUpdate = (partialProductSector: Partial<ProductSector>) => {
    dispatch(
      WorkflowActions.createEntityUpdateReferenceData<ProductSector>({
        entityId: productSector.id,
        entityType: EntityTypeEnum.PRODUCT_SECTOR,
        updatedEntity: {
          ...productSector,
          ...partialProductSector
        },
        workflowId
      })
    )
  }

  const handleSave = async () => {
    const originalProductSector = getProductSector(entityId)
    const originalProduct = originalProductSector && getProduct(originalProductSector?.productId)
    const micHasChanged = originalProduct && product ? originalProduct.micCode !== product.micCode : false

    if (micHasChanged) {
      const messageText = Locale.translate(Translation.SC_COLLATERAL_REPORTING_WARNING_MESSAGE)
      const result = await showDialog({
        type: NotificationType.Confirm,
        title: Translation.TC_COLLATERAL_SAVE_CONFIRMATION,
        messageText: `${messageText} \n \n Are you sure you want to continue?`,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO
      })

      if (result === Translation.TC_BUTTON_YES) {
        dispatch(WorkflowActions.createSubmitWorkflow(workflowId))
        if (onClose) {
          onClose()
        }
      }
    } else {
      dispatch(WorkflowActions.createSubmitWorkflow(workflowId))
      if (onClose) {
        onClose()
      }
    }
  }

  const handleClose = () => {
    dispatch(WorkflowActions.createCancelWorkflow(workflowId))
    if (onClose) {
      onClose()
    }
  }

  const hasProductSectorBasketType = !!productSector?.basketType

  const isGcBespoke = productSector?.basketType === CollateralBasketType.GC_BESPOKE
  const isDBV = productSector?.basketType === CollateralBasketType.DBV
  return (
    <SectorDialogComponent
      title={title}
      productSector={productSector}
      getProduct={getProduct}
      readOnly={{
        name: cannotUpdate,
        productId:
          (productSectorEntity.entityState === EntityState.EXISTING &&
            (initialMarket?.marketStatus === MarketStatus.MARKET_OPEN || initialMarket?.marketStatus === MarketStatus.MARKET_PRE_OPEN)) ||
          cannotUpdate,
        basisConvention: cannotUpdate || isGcBespoke || isDBV,
        basketType: cannotUpdate || productSectorEntity.entityState === EntityState.EXISTING,
        basketCurrency: cannotUpdate || !hasProductSectorBasketType || productSectorEntity.entityState === EntityState.EXISTING,
        defaultRepoType: cannotUpdate,
        startConsiderationOverrideThreshold: cannotUpdate,
        isExDividend: cannotUpdate || hasProductSectorBasketType || productSectorEntity.entityState === EntityState.EXISTING,
        exDividendDayConvention: cannotUpdate || productSectorEntity.entityState === EntityState.EXISTING,
        exDividendDays: cannotUpdate || productSectorEntity.entityState === EntityState.EXISTING,
        defaultNotificationDeadlineTime: cannotUpdate,
        defaultNoticePeriod: cannotUpdate,
        defaultFloatCrystallisationDaysByReferenceRateId: cannotUpdate,
        status: cannotUpdate
      }}
      validationIssues={productSectorEntity.validationIssues ?? []}
      onSave={handleSave}
      onUpdate={handleUpdate}
      onClose={handleClose}
    />
  )
})
